import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {ErrorService, MomentService, Notification, NotificationService, UiConfigService} from '@lv1871/core';
import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subscription, switchMap} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, take, tap} from 'rxjs/operators';
import {EventService} from './event.service';
import {flatMap, ListControl, UIArtefakt, UiBaseControl, Wertemenge} from '../models/uiArtefakt';
import {Fondsgruppe} from '../models/fondsgruppe';
import {Fonds} from '../models/fonds';
import {FondsgruppeItem} from '../models/fondsgruppeItem';
import {FondsAnlageKontext} from '../models/fondsAnlageKontext';
import {UiControl} from '../models/uiControl';
import {FondsMitAnteil} from '../models/fondsMitAnteil';
import {MatTableDataSource} from '@angular/material/table';
import {FondsgruppeInProdukt} from '../models/fondsgruppenInProdukt';
import {IndividualFonds} from '../components/fonds/fondstable-individuelles-portfolio/fonds-table-individuelles-portfolio.component';
import {FondsPortfolioData} from '../models/fondsPortfolioData';
import {Fondsgruppenart} from '../models/fondsgruppenart ';
import {FondsVermittlerspezifischesPortfolio} from '../models/fondsVermittlerspezifischesPortfolio';
import {StageConfigService} from './stage-config.service';

@Injectable()
export class FondsService implements OnDestroy {
  public unmanagedFonds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  public unmanagedFondsZuzahlung$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  public fondsPortfolioData$: BehaviorSubject<FondsPortfolioData> = new BehaviorSubject<FondsPortfolioData>(null);
  public fondsPortfolioDataObs$: Observable<[Fonds[], Fonds[], Fondsgruppe[], UiControl]>;
  public fondsCache$: BehaviorSubject<Fonds[]> = new BehaviorSubject<Fonds[]>(null);
  public fetchFondsgruppenUndFonds$: Observable<[Fonds[], Fondsgruppe[]]>;

  private _unmanagedFondsgruppenIdZuzahlung$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private _unmanagedFondsgruppenId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private _isPendingSubsciption: Subscription;
  private _fondsPortfolioDataSubsciption: Subscription;
  private _fondsgruppenCache$: BehaviorSubject<Fondsgruppe[]> = new BehaviorSubject<Fondsgruppe[]>(null);
  private _fondsCacheMitGeschlossenen$: BehaviorSubject<Fonds[]> = new BehaviorSubject<Fonds[]>(null);

  constructor(private _http: HttpClient, private _eventService: EventService,
              private _errorService: ErrorService, private _momentService: MomentService,
              private _stageConfigService: StageConfigService,
              private _notificationService: NotificationService,
              private _uiConfigService: UiConfigService) {

    this.fondsPortfolioDataObs$ = combineLatest<[Fonds[], Fonds[], Fondsgruppe[], UiControl]>([
      this.getAllFonds(), this.getAllFondsMitGeschlossenen(), this.getAlleFondsgruppen(),
      this._eventService.uiControls$]).pipe(
        filter(([fonds, fondsGeschlossenen, fondsgruppe, _]) => !!fonds && !!fondsgruppe),
        tap(([fonds, fondsGeschlossenen, fondsgruppe, uiControl]) => {
          const portfolioData = this._getFondsPortfolioData(fonds, fondsgruppe, uiControl);
          this.fondsPortfolioData$.next(portfolioData);
        })
    );

    this.fetchFondsgruppenUndFonds$ = this._eventService.versicherungsbeginn$.pipe(
        filter(versicherungsbeginn => !!versicherungsbeginn),
        distinctUntilChanged(),
        switchMap(versicherungsbeginn => {
          const headers = new HttpHeaders({
            'Content-Type': 'application/json',
          });
          const queryParams = { stichtag: versicherungsbeginn };
          return combineLatest<[Fonds[], Fondsgruppe[]]>(
              [this._http.get<Fonds[]>(this._buildFonds(), {params: queryParams}).pipe(tap(resp => this.fondsCache$.next(resp))),
                this._http.get<Fondsgruppe[]>(this._buildFondsgruppenUrl(), {params: queryParams}).pipe(
                    tap(resp => this._fondsgruppenCache$.next(resp)))
              ]
          );
        }),
        take(1)
    );
  }

  public ngOnDestroy() {
    this._isPendingSubsciption.unsubscribe();
    this._fondsPortfolioDataSubsciption.unsubscribe();
  }

  public getAnlaufAblaufmanagementFonds(fonds: Fonds[], listControl: ListControl): FondsMitAnteil {
    if (!fonds || listControl?.UIArtefakt?.length <= 1) {
      return null;
    }
    const bereitsSelektierterFonds = {} as FondsMitAnteil;
    listControl.UIArtefakt.forEach(fondsSel => {
      const wpid = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/wpid')[0];
      const anteil = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/fondsaufteilungssatz')[0];
      if (!!wpid?.WertEingabe) {
        bereitsSelektierterFonds.fondsId = wpid.WertEingabe;
        bereitsSelektierterFonds.prozentualerAnteil = anteil.WertEingabe;
      }
    });
    const ablaufFonds = fonds.filter(einzelfonds =>
        einzelfonds.fondsId + '' === bereitsSelektierterFonds.fondsId + '')[0] as FondsMitAnteil;
    ablaufFonds.prozentualerAnteil = Math.round(bereitsSelektierterFonds.prozentualerAnteil * 100);
    ablaufFonds.factsheet = this.getLink('Factsheet', ablaufFonds.fondsId);
    ablaufFonds.esgreport = this.getLink('ESG-Report', ablaufFonds.fondsId);
    return ablaufFonds;
  }

  public getFondsMitAnteilen(listControl: ListControl): FondsMitAnteil[] {
    const fondsMitAnteilArray: FondsMitAnteil[] = [];
    listControl.UIArtefakt.forEach(fondsSel => {
      const wpid = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/wpid')[0];
      const anteil = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/fondsaufteilungssatz')[0];
      if (!!wpid?.WertEingabe) {
        const fondsMitAnteil = {} as FondsMitAnteil;
        fondsMitAnteil.fondsId = wpid.WertEingabe;
        fondsMitAnteil.prozentualerAnteil = Math.round(anteil.WertEingabe * 100);
        fondsMitAnteilArray.push(fondsMitAnteil);
      }
    });
    return fondsMitAnteilArray;
  }

// Gibt die Fonds zurück, die fürs Anlaufmanagement erlaubt sind
  public getAnlaufmanagementFonds(): Observable<Fonds[]> {
    return combineLatest<[UiControl, Fondsgruppe[], Fonds[]]>([this._eventService.uiControls$,
      this._fondsgruppenCache$, this.fondsCache$]).pipe(
        filter(([uiControl, fondsgruppen, fonds]) => !!uiControl && !!fondsgruppen && !!fonds),
        map(([uiControl, fondsgruppen, fonds]) => {
          const anlaufmanagementArtefakt = flatMap(uiControl.uiVorgang.UIArtefakt).filter(uiArtefakt =>
              '/individuellePortfolioloesungAnlaufmanagement' === uiArtefakt.FeldnameExtern)[0] as Wertemenge;
          if (anlaufmanagementArtefakt.Werteliste.length !== 1) {
            this._errorService.pushErrorMessage('Das UIArtefakt ' + anlaufmanagementArtefakt.Identifikator + ' hat statt genau einem Element ' +
                +anlaufmanagementArtefakt.Werteliste.length + ' Elemente in seiner Werteliste.');
          }
          const fondsId = anlaufmanagementArtefakt.Werteliste[0].WertEingabe;
          anlaufmanagementArtefakt.WertEingabe = fondsId;
          const fondsgruppe = fondsgruppen.filter(gruppe => gruppe.fondsgruppeId + '' === fondsId + '')[0];
          if (!fondsgruppe) {
            this._errorService.pushErrorMessage('Die Fondsgruppe mit ID=' + fondsId + ' existiert nicht.');
          }

          if (!fondsgruppe.fondsgruppeItems || !fondsgruppe.fondsgruppeItems.length) {
            this._errorService.pushErrorMessage('Die Fondsgruppe mit ID=' + fondsId + ' hat keine Elemente fürs Anlaufmanagement.');
          }
          const fondsgruppeItems = [] as Fonds[];
          fondsgruppe.fondsgruppeItems.forEach(item => {
            const anlaufFonds = fonds.filter(einzelfonds => einzelfonds.fondsId + '' === item.fondsId + '')[0];
            if (!anlaufFonds) {
              this._errorService.pushErrorMessage('Der Fonds mit ID=' + item.fondsId + ' existiert nicht.');
            } else {
              fondsgruppeItems.push(anlaufFonds);
            }
          });
          return fondsgruppeItems;
        }),
        take(1)
    );
  }

  public getFondsgruppenInProdukt(gemanagtePortfolioloesung: Wertemenge): Observable<FondsgruppeInProdukt> {
    const fondsgruppeInProdukt = {} as FondsgruppeInProdukt;
    return this._fondsgruppenCache$.pipe(
        take(1),
        filter(_ => !!this._eventService.versicherungsbeginn$.getValue()),
        switchMap(fondsgruppen => {
          if (!!fondsgruppen) {
            return of(fondsgruppen);
          }
          const versicherungsbeginn = this._eventService.versicherungsbeginn$.getValue();
          const queryParams = { stichtag: versicherungsbeginn };
          return this._http.get<Fondsgruppe[]>(this._buildFondsgruppenUrl(), {params: queryParams}).pipe(
              filter(resp => !!resp),
              tap(resp => this._fondsgruppenCache$.next(resp))
          );
        }),
        map(response => {
          const managedFonds = [] as Fondsgruppe[];
          const unmanagedFonds = [] as Fondsgruppe[];
          gemanagtePortfolioloesung.Werteliste.forEach(element => {
            const fondsgruppe = response.filter(gruppe => gruppe.fondsgruppeId + '' === element.WertEingabe + '')[0];
            if (!!fondsgruppe) {
              if (!!fondsgruppe.managedFondsgroup) {
                managedFonds.push(fondsgruppe);
              } else {
                unmanagedFonds.push(fondsgruppe);
                if ('/gemanagtePortfolioloesung' === gemanagtePortfolioloesung.FeldnameExtern ||
                    '/individuellePortfolioloesung' === gemanagtePortfolioloesung.FeldnameExtern) {
                  this._unmanagedFondsgruppenId$.next(fondsgruppe.fondsgruppeId);
                }
                if ('/gemanagtePortfolioloesungZuzahlung' === gemanagtePortfolioloesung.FeldnameExtern ||
                    '/individuellePortfolioloesungZuzahlung' === gemanagtePortfolioloesung.FeldnameExtern) {
                  this._unmanagedFondsgruppenIdZuzahlung$.next(fondsgruppe.fondsgruppeId);
                }
              }
            }
          });
          const unmanagedFondsIds = [] as number[];
          if (unmanagedFonds.length > 1) {
            this._errorService.pushErrorMessage('Es darf nur genau eine individuelle Fondsgruppe am Produkt konfigurirt sein. ' +
                'Es sind aber ' + unmanagedFonds.length + ' mit den Ids: ' + unmanagedFonds.join());
          }
          unmanagedFonds.forEach(gruppe => gruppe.fondsgruppeItems.forEach(item =>
              unmanagedFondsIds.push(item.fondsId)));
          if ('/gemanagtePortfolioloesung' === gemanagtePortfolioloesung.FeldnameExtern ||
              '/individuellePortfolioloesung' === gemanagtePortfolioloesung.FeldnameExtern) {
            this.unmanagedFonds$.next(unmanagedFondsIds);
          }
          if ('/gemanagtePortfolioloesungZuzahlung' === gemanagtePortfolioloesung.FeldnameExtern ||
              '/individuellePortfolioloesungZuzahlung' === gemanagtePortfolioloesung.FeldnameExtern) {
            this.unmanagedFondsZuzahlung$.next(unmanagedFondsIds);
          }
          fondsgruppeInProdukt.fondsgruppeIndividuell = unmanagedFondsIds;
          //return managedFonds;

          const fondsgruppen: Map<string, Fondsgruppe[]> = new Map();
          managedFonds.forEach(fonds => {
            const gruppierungsId = fonds.portfolio?.portfolioCluster?.id;
            if (!gruppierungsId) {
              fondsgruppen.set(fonds.name, [fonds]);
            } else {
              if (!fondsgruppen.get(gruppierungsId + '')) {
                fondsgruppen.set(gruppierungsId + '', [fonds]);
              } else {
                const savedFonds = fondsgruppen.get(gruppierungsId + '');
                savedFonds.push(fonds);
              }
            }
          });
          fondsgruppen.forEach((val, key) => {
            if (val.length !== 1) {
              val.sort((fondsgruppe1, fondsgruppe2) =>
                  fondsgruppe1.portfolio.anlagestil < fondsgruppe2.portfolio.anlagestil ? -1 : 1);
            }
          });
          fondsgruppeInProdukt.fondsgruppeManaged = fondsgruppen;
          return fondsgruppeInProdukt;
        }),
        catchError((error) => {
          if (typeof (error.error) === 'string') {
            this._errorService.pushErrorMessage(error.error);
          } else {
            this._errorService.pushErrorMessage(error.message);
          }
          return of(null);
        }));
  }

  public getAlleFondsgruppen(): Observable<Fondsgruppe[]> {
    return this._fondsgruppenCache$.pipe(
        filter(_ => !!this._eventService.versicherungsbeginn$.getValue()),
        switchMap(fondsgruppen => {
          if (!!fondsgruppen) {
            return of(fondsgruppen);
          }
          const versicherungsbeginn = this._eventService.versicherungsbeginn$.getValue();
          const queryParams = { stichtag: versicherungsbeginn };
          return this._http.get<Fondsgruppe[]>(this._buildFondsgruppenUrl(), {params: queryParams}).pipe(
              tap(resp => this._fondsgruppenCache$.next(resp))
          );
        }));
  }

  public getAllFonds(): Observable<Fonds[]> {
    return this.fondsCache$.pipe(
        filter(_ => !!this._eventService.versicherungsbeginn$.getValue()),
        switchMap(fonds => {
          if (!!fonds) {
            return of(fonds);
          }
          const versicherungsbeginn = this._eventService.versicherungsbeginn$.getValue();
          const queryParams = { stichtag: versicherungsbeginn };
          return this._http.get<Fonds[]>(this._buildFonds(), {params: queryParams}).pipe(
              tap(resp => this.fondsCache$.next(resp))
          );
        }));
  }

  public getAllFondsMitGeschlossenen(): Observable<Fonds[]> {
    return this._fondsCacheMitGeschlossenen$.pipe(
        filter(_ => !!this._eventService.versicherungsbeginn$.getValue()),
        switchMap(fonds => {
          if (!!fonds) {
            return of(fonds);
          }
          const versicherungsbeginn = this._eventService.versicherungsbeginn$.getValue();
          const queryParams = { stichtag: versicherungsbeginn };
          return this._http.get<Fonds[]>(this._buildFondsMitGeschlossenen(), {params: queryParams}).pipe(
              tap(resp => this._fondsCacheMitGeschlossenen$.next(resp))
          );
        }));
  }

  public getAllFondsSorted(suchbegriff: any): Observable<Fonds[]> {
    if (!suchbegriff || !this._eventService.versicherungsbeginn$.getValue()) {
      return this.getAllFonds();
    } else {
      const versicherungsbeginn = this._eventService.versicherungsbeginn$.getValue();
      const queryParams = { stichtag: versicherungsbeginn, suchbegriff };
      return this._http.get<Fonds[]>(this._buildFondsSorted(), {params: queryParams});
    }
  }

  public saveVermittlerspezifischesPortfolio(payload: FondsVermittlerspezifischesPortfolio):
      Observable<FondsVermittlerspezifischesPortfolio> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.post<FondsVermittlerspezifischesPortfolio>(this._buildVermittlerspezifischesPortfolioSave(),
        payload, {headers}).pipe(
        map(result => {
          const notification = new Notification(
              'Alert', 'Positive', null,
              'Portfolio wurde gespeichert.',
              false, 3000);
          this._notificationService.addNotification(notification);
          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          const notification = new Notification(
              'Alert', 'Danger', null, 'Portfolio konnte nicht gespeichert werden.',
              true, null);
          this._notificationService.addNotification(notification);
          return of(null);
        })
    );
  }

  public updateVermittlerspezifischesPortfolio(payload: FondsVermittlerspezifischesPortfolio, id: number):
      Observable<FondsVermittlerspezifischesPortfolio> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.post<FondsVermittlerspezifischesPortfolio>(this._buildVermittlerspezifischesPortfolioUpdate(id),
        payload, {headers}).pipe(
        map(result => {
          const notification = new Notification(
              'Alert', 'Positive', null,
              'Portfolio wurde gespeichert.',
              false, 3000);
          this._notificationService.addNotification(notification);
          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          const notification = new Notification(
              'Alert', 'Danger', null, 'Portfolio konnte nicht gespeichert werden.',
              true, null);
          this._notificationService.addNotification(notification);
          return of(null);
        })
    );
  }

  public deleteAllVermittlerspezifischePortfolio(): Observable<unknown> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.post<unknown>(this._buildDeleteVermittlerspezifischePortfolio(), {headers}).pipe(
        map(result => result),
        catchError((error: HttpErrorResponse) => {
          if (this._stageConfigService.isVermittlerspezifischesPortfolioMoeglich()) {
            const notification = new Notification(
                'Alert', 'Danger', null, 'Fehler beim Löschen',
                true, null);
            this._notificationService.addNotification(notification);
          }
          return of(null);
        })
    );
  }

  public createVermittlerspezifischesPortfolioTestdata(): Observable<unknown> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.post<unknown>(this._buildCreateVermittlerspezifischePortfolioTestdata(), {headers}).pipe(
        map(result => result),
        catchError((error: HttpErrorResponse) => {
          if (this._stageConfigService.isVermittlerspezifischesPortfolioMoeglich()) {
            const notification = new Notification(
                'Alert', 'Danger', null, 'Fehler beim Erstellen',
                true, null);
            this._notificationService.addNotification(notification);
          }
          return of(null);
        })
    );
  }

  public getGespeicherteVermittlerspezifischePortfolios(): Observable<FondsVermittlerspezifischesPortfolio[]> {
    if (!this._stageConfigService.isVermittlerspezifischesPortfolioMoeglich()) {
      return of(null);
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<FondsVermittlerspezifischesPortfolio[]>(this._buildVermittlerspezifischesPortfolio(), {headers}).pipe(
        map(result => result),
        catchError((error: HttpErrorResponse) => {
          if (this._stageConfigService.isVermittlerspezifischesPortfolioMoeglich()) {
            const notification = new Notification(
                'Alert', 'Danger', null, 'Ihre Portfoliovorlagen können aktuell nicht geladen werden.',
                true, null);
            this._notificationService.addNotification(notification);
          }
          return of(null);
        })
    );
  }

  updateFondsInUiControl(fondsgruppeItems: FondsgruppeItem, portfolioArtefakt: UIArtefakt) {
    portfolioArtefakt.UIArtefakt.forEach(artefakt => {
      let wpArtefakt: UIArtefakt;
      let anteilArtefakt: UIArtefakt;
      artefakt.UIArtefakt.forEach(artef => {
        if (artef.FeldnameExtern === '/wpid') {
          wpArtefakt = artef;
        }
        if (artef.FeldnameExtern === '/fondsaufteilungssatz') {
          anteilArtefakt = artef;
        }
      });
      if (wpArtefakt.WertEingabe === fondsgruppeItems.fondsId) {
        anteilArtefakt.WertEingabe = this.getProzentAsDouble(fondsgruppeItems.prozentualerAnteil);
      }
    });
  }

  updateVermittlerspezifischesPortfolioInUiControl(id: number | null, kontext: FondsAnlageKontext) {
    let feldnameExtern = '/PortfoliovorlageID';
    if (FondsAnlageKontext.ZUZAHLUNGSBETRAG === kontext) {
      feldnameExtern = '/PortfoliovorlageZuzahlungID';
    }
    this._eventService.uiControls$.pipe(
        tap(control => flatMap(control.uiVorgang.UIArtefakt).forEach(uiArtefakt => {
          if (feldnameExtern === uiArtefakt.FeldnameExtern) {
            uiArtefakt.WertEingabe = id;
          }
        })),
        take(1)
    ).subscribe();
  }

  manageAbweichungVermittlerspezifischesPortfolio(kontext: FondsAnlageKontext) {
    const kontextBezeichnung = this.getAnlageKontextAsString(kontext);
    const notification = new Notification(
        'Toast', 'Info',
        'Portfoliovorlage geändert',
        'Sie haben die in der <b>' + kontextBezeichnung + '</b> verwendete Portfoliovorlage zwischenzeitlich geändert. ' +
        'Dieses Angebot enthält weiterhin die ursprünglich ausgewählten Fonds.',
        true, null);
    this._notificationService.addNotification(notification);
    this.updateVermittlerspezifischesPortfolioInUiControl(null, kontext);
  }

  setSelectedFondsInUiControl(fondsgruppeItems: FondsgruppeItem[], anlageKontext: FondsAnlageKontext, fondsArtefakt?: UIArtefakt) {
    return  this._unmanagedFondsgruppenId$.pipe(
        switchMap(unmanagedFondsgruppenId => this._eventService.uiControls$.pipe(
            tap(uiControl => {
              let isIndividuell = false;
              let portfolioArtefakt = fondsArtefakt;
              const feldnameExtern = this._getFeldnameExternForPortfolioListe(anlageKontext);
              flatMap(uiControl.uiVorgang.UIArtefakt).forEach(uiArtefakt => {
                if (feldnameExtern === uiArtefakt.FeldnameExtern) {
                  portfolioArtefakt = uiArtefakt;
                }
                if (uiArtefakt.FeldnameExtern === '/individuellePortfolioloesung' ||
                    uiArtefakt.FeldnameExtern === '/individuellePortfolioloesungZuzahlung') {
                  uiArtefakt.WertEingabe = unmanagedFondsgruppenId + '';
                  isIndividuell = true;
                }
              });

              if (!!portfolioArtefakt) {
                const portfolioTemplate = portfolioArtefakt.UIArtefakt[0];
                const bereitsSelektierteFondsMap: Map<number, number> = new Map<number, number>();
                portfolioArtefakt.UIArtefakt.forEach(fondsSel => {
                  const wpid = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/wpid')[0];
                  const anteil = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/fondsaufteilungssatz')[0];
                  if (!!wpid?.WertEingabe) {
                    bereitsSelektierteFondsMap.set(wpid.WertEingabe, anteil.WertEingabe);
                  }
                });
                portfolioArtefakt.UIArtefakt = portfolioArtefakt.UIArtefakt.slice(0, 1);
                fondsgruppeItems?.forEach(fondsgruppeItem => {
                  const newPortfolioListenArtefakt = new UIArtefakt();
                  newPortfolioListenArtefakt.Identifikator = portfolioTemplate.Identifikator;
                  newPortfolioListenArtefakt.IdentifikatorUuid = portfolioTemplate.IdentifikatorUuid;
                  newPortfolioListenArtefakt.UIArtefakttyp = portfolioTemplate.UIArtefakttyp;
                  newPortfolioListenArtefakt.UIArtefakt = [];
                  portfolioArtefakt.UIArtefakt.push(newPortfolioListenArtefakt);

                  flatMap(portfolioTemplate.UIArtefakt).forEach(uiArtefakt => {
                    if (uiArtefakt.FeldnameExtern === '/wpid' ||
                        uiArtefakt.FeldnameExtern === '/fondsaufteilungssatz') {
                      const wpArtefakt = new UIArtefakt();
                      wpArtefakt.Identifikator = uiArtefakt.Identifikator;
                      wpArtefakt.IdentifikatorUuid = uiArtefakt.IdentifikatorUuid;
                      wpArtefakt.UIArtefakttyp = uiArtefakt.UIArtefakttyp;
                      wpArtefakt.FeldnameExtern = uiArtefakt.FeldnameExtern;
                      if (uiArtefakt.FeldnameExtern === '/wpid') {
                        wpArtefakt.WertEingabe = fondsgruppeItem.fondsId;
                      }
                      if (uiArtefakt.FeldnameExtern === '/fondsaufteilungssatz') {

                        let anteil = this.getProzentAsDouble(fondsgruppeItem.prozentualerAnteil);

                        if (isIndividuell) {
                          const anteilFuerIndividuellGewaehlt = bereitsSelektierteFondsMap?.get(fondsgruppeItem.fondsId);
                          if (!!anteilFuerIndividuellGewaehlt) {
                            anteil = anteilFuerIndividuellGewaehlt;
                          }
                        }
                        wpArtefakt.WertEingabe = anteil;
                      }
                      newPortfolioListenArtefakt.UIArtefakt.push(wpArtefakt);
                    }
                  });
                });
              }
            }),
            take(1))
        ),
        take(1));
  }

  getAnteilGleichgewichtet(length: number, index: number): number {
    switch (length) {
      case 0:
        return 0;
      case 1:
        return 1;
      case 2:
        return 0.5;
      case 3:
        return index === 0 ? 0.34 : 0.33;
      case 4:
        return 0.25;
      case 5:
        return 0.2;
      case 6:
        return index < 4 ? 0.17 : 0.16;
      case 7:
        return index < 2 ? 0.15 : 0.14;
      case 8:
        return index < 4 ? 0.13 : 0.12;
      case 9:
        return index < 1 ? 0.12 : 0.11;
      case 10:
        return 0.1;
      case 11:
        return index < 10 ? 0.09 : 0.1;
      case 12:
        return index < 4 ? 0.09 : 0.08;
      case 13:
        return index < 9 ? 0.08 : 0.07;
      case 14:
        return index < 2 ? 0.08 : 0.07;
      case 15:
        return index < 10 ? 0.07 : 0.06;
      case 16:
        return index < 4 ? 0.07 : 0.06;
      case 17:
        return index < 15 ? 0.06 : 0.05;
      case 18:
        return index < 10 ? 0.06 : 0.05;
      case 19:
        return index < 5 ? 0.06 : 0.05;
      case 20:
        return 0.05;
      default:
        return 0.05;
    }
  }

  public getAnlageKontext(anlageKontext: string): FondsAnlageKontext {
    if (anlageKontext === '/anlagestrategieZuzahlung' || anlageKontext === '/listePortfolioZuzahlung' ||
        anlageKontext === '/gemanagtePortfolioloesungZuzahlung' || anlageKontext === '/PortfoliovorlageZuzahlungID') {
      return FondsAnlageKontext.ZUZAHLUNGSBETRAG;
    }
    if (anlageKontext === '/anlagestrategie' || anlageKontext === '/listePortfolio' ||
        anlageKontext === '/gemanagtePortfolioloesung' || anlageKontext === '/PortfoliovorlageID') {
      return FondsAnlageKontext.INVESTITIONSBETRAG;
    }
    return null;
  }

  public addAnlaufAblaufmanagementFonds(fondsArtefaktListe: UIArtefakt, fondsId: number) {
    fondsArtefaktListe.UIArtefakt = fondsArtefaktListe.UIArtefakt.slice(0, 1);

    const newListenElementArtefakt = new UIArtefakt();
    newListenElementArtefakt.Identifikator = fondsArtefaktListe.UIArtefakt[0].Identifikator;
    newListenElementArtefakt.IdentifikatorUuid = fondsArtefaktListe.UIArtefakt[0].IdentifikatorUuid;
    newListenElementArtefakt.UIArtefakttyp = fondsArtefaktListe.UIArtefakt[0].UIArtefakttyp;
    newListenElementArtefakt.UIArtefakt = [];
    fondsArtefaktListe.UIArtefakt.push(newListenElementArtefakt);

    flatMap(fondsArtefaktListe.UIArtefakt).forEach(uiArtefakt => {
      if (uiArtefakt.FeldnameExtern === '/wpid' ||
          uiArtefakt.FeldnameExtern === '/fondsaufteilungssatz') {
        const wpArtefakt = new UIArtefakt();
        wpArtefakt.Identifikator = uiArtefakt.Identifikator;
        wpArtefakt.IdentifikatorUuid = uiArtefakt.IdentifikatorUuid;
        wpArtefakt.UIArtefakttyp = uiArtefakt.UIArtefakttyp;
        wpArtefakt.FeldnameExtern = uiArtefakt.FeldnameExtern;
        if (uiArtefakt.FeldnameExtern === '/wpid') {
          wpArtefakt.WertEingabe = fondsId;
        }
        if (uiArtefakt.FeldnameExtern === '/fondsaufteilungssatz') {
          wpArtefakt.WertEingabe = 1;
        }
        newListenElementArtefakt.UIArtefakt.push(wpArtefakt);
      }
      ;
    });
  }

  public getProzentAsDouble(prozentZahl: number): number {
    if (!prozentZahl) {
      return void 0;
    }
    const newProzentWert = prozentZahl * 0.01;
    const newProzentWertRounded = Math.round(newProzentWert * 100) / 100;
    return newProzentWertRounded;
  }

  public getLink(schriftstueckArt: string, fondsId: number, fondsgruppenart?: Fondsgruppenart): string {
    if (!fondsgruppenart) {
      fondsgruppenart = Fondsgruppenart.FONDS;
    }
    return this._uiConfigService.getAntragserfassungAnonUrl() + '/api/fondsdokument/' + schriftstueckArt + '/' + fondsId + '/' + fondsgruppenart;
  }

  public getRisikoklasseDurchschnitt(selectedFonds: UIArtefakt[], data: IndividualFonds[]): number {
    let risikoklasseGesamtNeu = 0;
    let anteileAufsummiert = 0;
    let anteilNichtVorhanden: boolean;
    selectedFonds.forEach(fondsSel => {
      const wpid = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/wpid')[0];
      const anteil = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/fondsaufteilungssatz')[0];
      if (!wpid || !anteil) {
        return;
      }
      if (!!wpid.WertEingabe && !anteil.WertEingabe) {
        anteilNichtVorhanden = true;
      }
      const risikoklasse = data.filter(fonds => fonds.fondsId + '' === wpid.WertEingabe + '')[0]?.risikoklasse;
      if (!!anteil.WertEingabe) {
        anteileAufsummiert += anteil.WertEingabe;
        risikoklasseGesamtNeu += anteil.WertEingabe * risikoklasse;
      }
    });
    if (anteilNichtVorhanden) {
      return 0;
    }
    anteileAufsummiert = Math.round(anteileAufsummiert);
    risikoklasseGesamtNeu = Math.round(risikoklasseGesamtNeu);
    if (anteileAufsummiert === 1) {
      return risikoklasseGesamtNeu;
    } else {
      return 0;
    }
  }

  public isPortfolio1AndPortfolio2Equal(portfolio1: FondsgruppeItem[] | FondsMitAnteil[],
                                        portfolio2: FondsgruppeItem[] | FondsMitAnteil[]): boolean {
    if (!portfolio1 || !portfolio2) {
      return false;
    }
    let tablePortfolio1: string = '';
    let tablePortfolio2: string = '';
    portfolio1.sort((a, b) => a.fondsId - b.fondsId);
    portfolio2.sort((a, b) => a.fondsId - b.fondsId);

    portfolio1.forEach(element => {
      tablePortfolio1 += element.fondsId;
      tablePortfolio1 += element.prozentualerAnteil;
    });

    portfolio2.forEach(element => {
      tablePortfolio2 += element.fondsId;
      tablePortfolio2 += element.prozentualerAnteil;
    });
    return tablePortfolio1 === tablePortfolio2;
  }

  public getAnlageKontextAsString(kontext: FondsAnlageKontext): string {
    if (kontext === FondsAnlageKontext.INVESTITIONSBETRAG) {
      return 'Investitionsbetrag';
    } else if (kontext === FondsAnlageKontext.ZUZAHLUNGSBETRAG) {
      return 'Zuzahlung';
    }
    return 'unbekannt';
  }

  private _getFondsPortfolioData(fonds: Fonds[], fondsgruppe: Fondsgruppe[], uiControl: UiControl): FondsPortfolioData {
    const data = {} as FondsPortfolioData;
    flatMap(uiControl.uiVorgang.UIArtefakt).forEach(artefakt => {
      if ('/gemanagtePortfolioloesung' === artefakt.FeldnameExtern) {
        data.isAnlagestrategieGemanaged = true;
        const foundFondsgruppe = fondsgruppe.filter(
            gruppe => gruppe.fondsgruppeId + '' === artefakt.WertEingabe + '')[0];
        if (!!foundFondsgruppe) {
          data.fondsgruppeNameInvestitionsbetrag = foundFondsgruppe.name;
          data.urlInvestitionsbetrag = foundFondsgruppe.portfolio?.url;
          data.artikelInvestitionsbetrag = foundFondsgruppe.portfolio?.esgEinstufungTvo;
          data.urlInvestitionsbetragFactsheet = this.getLink('Factsheet', (foundFondsgruppe.fondsgruppeId), Fondsgruppenart.FONDSGRUPPE);
          data.urlInvestitionsbetragESGReport = this.getLink('ESG-Report', (foundFondsgruppe.fondsgruppeId), Fondsgruppenart.FONDSGRUPPE);
        }
      }
      if ('/individuellePortfolioloesung' === artefakt.FeldnameExtern) {
        data.isAnlagestrategieIndividuell = true;
      }
      if ('/gemanagtePortfolioloesungZuzahlung' === artefakt.FeldnameExtern) {
        data.isAnlagestrategieGemanagedZuzahlung = true;
        const foundFondsgruppe = fondsgruppe.filter(
            gruppe => gruppe.fondsgruppeId + '' === artefakt.WertEingabe + '')[0];
        if (!!foundFondsgruppe) {
          data.fondsgruppeNameZuzahlung = foundFondsgruppe.name;
          data.urlZuzahlung = foundFondsgruppe.portfolio?.url;
          data.artikelZuzahlung = foundFondsgruppe.portfolio?.esgEinstufungTvo;
          data.urlZuzahlungFactsheet = this.getLink('Factsheet', (foundFondsgruppe.fondsgruppeId), Fondsgruppenart.FONDSGRUPPE);
          data.urlZuzahlungESGReport = this.getLink('ESG-Report', (foundFondsgruppe.fondsgruppeId), Fondsgruppenart.FONDSGRUPPE);
        }
      }
      if ('/individuellePortfolioloesungZuzahlung' === artefakt.FeldnameExtern) {
        data.isAnlagestrategieIndividuellZuzahlung = true;
      }
      if ('/anlaufmanagement' === artefakt.FeldnameExtern) {
        data.isAnlaufmanagementGewaehlt = artefakt.WertEingabe;
      }
      if ('/listePortfolio' === artefakt.FeldnameExtern) {
        data.anzahlFondsAnlagestrategieIndividuell = artefakt.UIArtefakt.length;
        const fondsForDataTableInvestitionsbetrag = this._getSelectedFonds(artefakt.UIArtefakt, fonds);
        data.donutChartDataInvestitionsbetrag = JSON.parse(JSON.stringify(fondsForDataTableInvestitionsbetrag));
        data.risikoklasseDurchschnittInvestitionsbetrag = this._getRisikoklasseDurchschnitt(fondsForDataTableInvestitionsbetrag);
        const summaryZeile = this.getSummaryZeile(fondsForDataTableInvestitionsbetrag);
        summaryZeile.factsheet = data.urlInvestitionsbetragFactsheet;
        summaryZeile.esgreport = data.urlInvestitionsbetragESGReport;
        summaryZeile.risikoklasse = data.risikoklasseDurchschnittInvestitionsbetrag;
        fondsForDataTableInvestitionsbetrag.sort((a, b) => b.prozentualerAnteil - a.prozentualerAnteil);
        fondsForDataTableInvestitionsbetrag.push(summaryZeile);
        data.dataSourceInvestitionsbetrag = new MatTableDataSource(fondsForDataTableInvestitionsbetrag);
      }
      if ('/listePortfolioZuzahlung' === artefakt.FeldnameExtern) {
        data.anzahlFondsAnlagestrategieIndividuellZuzahlung = artefakt.UIArtefakt.length;
        const fondsForDataTableZuzahlung = this._getSelectedFonds(artefakt.UIArtefakt, fonds);
        data.donutChartDataZuzahlung = JSON.parse(JSON.stringify(fondsForDataTableZuzahlung));
        data.risikoklasseDurchschnittZuzahlung = this._getRisikoklasseDurchschnitt(fondsForDataTableZuzahlung);
        const summaryZeile = this.getSummaryZeile(fondsForDataTableZuzahlung);
        summaryZeile.factsheet = data.urlZuzahlungFactsheet;
        summaryZeile.esgreport = data.urlZuzahlungESGReport;
        summaryZeile.risikoklasse = data.risikoklasseDurchschnittZuzahlung;
        fondsForDataTableZuzahlung.sort((a, b) => b.prozentualerAnteil - a.prozentualerAnteil);
        fondsForDataTableZuzahlung.push(summaryZeile);
        data.dataSourceZuzahlung = new MatTableDataSource(fondsForDataTableZuzahlung);
      }
      if ('/listePortfolioAnlaufmanagement' === artefakt.FeldnameExtern) {
        data.fondsForAnlaufmanagement = new MatTableDataSource(this._getSelectedFonds(artefakt.UIArtefakt, fonds));
      }

    });
    return data;
  }

  private getSummaryZeile(fondsForData: FondsMitAnteil[]) {
    let anteil = 0;
    fondsForData.forEach(fondsMitAnteil => {
      anteil += fondsMitAnteil.prozentualerAnteil;
    });
    const summaryZeile = {} as FondsMitAnteil;
    summaryZeile.prozentualerAnteil = Math.round(anteil);
    summaryZeile.name = 'Gesamtportfolio';
    return summaryZeile;
  }

  private _getRisikoklasseDurchschnitt(selectedFonds: FondsMitAnteil[]): number {
    let risikoklasseGesamtNeu = 0;
    let anteileAufsummiert = 0;
    selectedFonds.forEach(fondsSel => {
      const anteil = this.getProzentAsDouble(fondsSel.prozentualerAnteil);
      anteileAufsummiert += anteil;
      risikoklasseGesamtNeu += anteil * fondsSel.risikoklasse;
    });
    return Math.round(risikoklasseGesamtNeu);
  }

  private _getSelectedFonds(selectedFonds: UIArtefakt[], fonds: Fonds[]) {
    const fondsDataMap: Map<number, number> = new Map<number, number>();
    selectedFonds?.forEach(fondsSel => {
      const wpid = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/wpid')[0];
      const anteil = fondsSel.UIArtefakt.filter(fondsItem => fondsItem.FeldnameExtern === '/fondsaufteilungssatz')[0];
      if (!!wpid.WertEingabe) {
        fondsDataMap.set(wpid.WertEingabe, anteil.WertEingabe);
      }
    });

    const fondsForDataTable: FondsMitAnteil[] = [];
    fonds.forEach(einzelfonds => {
      let prozentWert;
      if (!!fondsDataMap.get(einzelfonds.fondsId)) {
        const einzelfondsKopie = JSON.parse(JSON.stringify(einzelfonds));
        prozentWert = Math.round(fondsDataMap.get(einzelfondsKopie.fondsId) * 100);
        const einzelFondsForDataTable = einzelfondsKopie as FondsMitAnteil;
        einzelFondsForDataTable.prozentualerAnteil = prozentWert;
        einzelFondsForDataTable.factsheet = this.getLink('Factsheet', einzelfondsKopie.fondsId);
        einzelFondsForDataTable.esgreport = this.getLink('ESG-Report', einzelfondsKopie.fondsId);
        fondsForDataTable.push(einzelFondsForDataTable);
      }
    });
    return fondsForDataTable;
  }

  private _getZeitraum(model: number): string {
    let value = '';
    const monat = model % 12;
    const jahr = Math.trunc(model / 12);
    if (!!jahr) {
      if (jahr > 1) {
        value = jahr + ' Jahre ';
      } else {
        value = jahr + ' Jahr ';
      }
    }
    if (!!monat) {
      if (monat > 1) {
        value += monat + ' Monate';
      } else {
        value += monat + ' Monat';
      }
    }
    return value;
  }

  private _buildFondsgruppenUrl(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/fondsgruppe';
  }

  private _buildFonds(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/fonds';
  }

  private _buildFondsMitGeschlossenen(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/fondsmitgeschlossenen';
  }

  private _buildFondsSorted(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt//fonds/suche';
  }

  private _buildDeleteVermittlerspezifischePortfolio(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/vermittlerspezifisch/deleteAll';
  }

  private _buildCreateVermittlerspezifischePortfolioTestdata(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/vermittlerspezifisch/testdata';
  }

  private _buildVermittlerspezifischesPortfolioSave(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/vermittlerspezifisch/save';
  }

  private _buildVermittlerspezifischesPortfolioUpdate(id: number): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/vermittlerspezifisch/update/' + id;
  }

  private _buildVermittlerspezifischesPortfolio(): string {
    const host = this._uiConfigService.getAntragserfassungUrl();
    return host + '/api/produkt/vermittlerspezifisch';
  }

  private _getFeldnameExternForPortfolioListe(anlageKontext: FondsAnlageKontext): string {
    if (anlageKontext === FondsAnlageKontext.INVESTITIONSBETRAG) {
      return '/listePortfolio';
    }
    if (anlageKontext === FondsAnlageKontext.ZUZAHLUNGSBETRAG) {
      return '/listePortfolioZuzahlung';
    }
    return null;
  }


}
